import React, { useState } from 'react';
import { dayjs } from 'app/dayjs';
import { renderToString } from 'react-dom/server';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';
import { Currency, CurrencyVariant } from 'components/core';
import { TimeFilterOption } from 'modules/dashboard/components';
import { useTracking, TrackEventName } from 'modules/tracking';
import { ReturnsChartTooltip } from './components/returns-chart-tooltip/returns-chart-tooltip';
import { Styled } from './returns-chart.style';

export interface MonthlyReturn {
  start_date: string;
  end_date: string;
  starting_balance: string;
  ending_balance: string;
  net_deposits: string;
  fees: string;
  returns_in_dollars: string;
  returns_percentage: string;
  returns_percentage_annualized: string;
  fee_shifted?: number;
  fee_date?: string;
  dividends?: number;
  dividends_shifted?: number;
  dividends_date?: string;
  interest?: number;
  interest_shifted?: number;
  interest_date?: string;
}

const getXAxisTickFormat = (timeFilter: TimeFilterOption) => {
  if (timeFilter === TimeFilterOption.ALL) {
    return 'MMM YYYY';
  }

  return 'MMMM';
};

const moneyFormatter = (value: number, currencyVariant: CurrencyVariant) => {
  return renderToString(
    <Currency
      decimalScale={0}
      number={value}
      renderText={(renderValue) => renderValue}
      variant={currencyVariant}
    />, // TODO: is this the best approach?
  );
};

const getMonthStart = (date: string) =>
  dayjs(date).startOf('month').format('YYYY-MM-01');

// Map a day into its month band on a [-0.5, n-0.5] numeric axis.
const getXPositionWithinMonth = (date: string, monthIndex: number) => {
  const day = dayjs(date);

  return monthIndex - 0.5 + (day.date() - 0.5) / day.daysInMonth();
};

// Keep overlay plot area aligned with the bar chart's visible y-axes.
const hiddenTickStyle = {
  ...Styled.TickStyle,
  fill: 'transparent',
};

// Zero-based when returns are all positive; extend downward for losses.
const getReturnsAxisMin = (dataMin: number) => Math.min(0, dataMin);

const getReturnsAxisMax = (dataMax: number) => dataMax * 2 || 1;

interface ReturnsChartProps {
  dailyAUMData: {
    amount: number;
    date: string;
  }[];

  monthlyReturnData: MonthlyReturn[];

  timeFilter: TimeFilterOption;

  startDate: string;

  endDate: string;
}

export const ReturnsChart: React.FC<ReturnsChartProps> = ({
  dailyAUMData,
  monthlyReturnData,
  timeFilter,
  startDate,
  endDate,
}) => {
  const { trackEvent } = useTracking<{
    component: string;
    eventName: TrackEventName;
    month: string;
  }>({
    component: 'Returns Chart',
  });
  const [trackTooltipMonth, setTrackTooltipMonth] = useState<string>();

  const monthlyReturnsByStartDate = new Map(
    [...(monthlyReturnData ?? [])]
      .sort((a, b) => (a.start_date > b.start_date ? 1 : -1))
      .map((monthlyReturn) => [
        getMonthStart(monthlyReturn.start_date),
        monthlyReturn,
      ]),
  );

  const monthStartsFromDailyData = Array.from(
    new Set(
      [...(dailyAUMData ?? [])]
        .filter(
          (dailyBalance) =>
            dailyBalance.amount > 0 &&
            dailyBalance.date >= startDate &&
            dailyBalance.date <= endDate,
        )
        .map((dailyBalance) => getMonthStart(dailyBalance.date)),
    ),
  );

  const chartMonthStarts = Array.from(
    new Set([
      ...Array.from(monthlyReturnsByStartDate.keys()),
      ...monthStartsFromDailyData,
    ]),
  ).sort();

  const sortedMonthlyData = chartMonthStarts.map((start_date, index) => {
    const monthlyReturn = monthlyReturnsByStartDate.get(start_date);

    return {
      ...(monthlyReturn ?? {
        start_date,
        end_date: dayjs(start_date).endOf('month').format('YYYY-MM-DD'),
        starting_balance: '',
        ending_balance: '',
        net_deposits: '',
        fees: '',
        returns_in_dollars: '0',
        returns_percentage: '',
        returns_percentage_annualized: '',
      }),
      returns_in_dollars: monthlyReturn
        ? Number(monthlyReturn.returns_in_dollars)
        : null,
      xIndex: index,
    };
  });

  const monthIndexByStartDate = new Map(
    sortedMonthlyData.map(({ start_date, xIndex }) => [start_date, xIndex]),
  );

  const sortedDailyData = [...(dailyAUMData ?? [])]
    .sort((a, b) => (a.date > b.date ? 1 : -1))
    .filter(
      (dailyBalance) =>
        dailyBalance.amount > 0 &&
        dailyBalance.date >= startDate &&
        dailyBalance.date <= endDate,
    )
    .flatMap((dailyBalance) => {
      const monthIndex = monthIndexByStartDate.get(
        getMonthStart(dailyBalance.date),
      );

      if (monthIndex === undefined) {
        return [];
      }

      return [
        {
          ...dailyBalance,
          xIndex: getXPositionWithinMonth(dailyBalance.date, monthIndex),
        },
      ];
    });

  const xDomain =
    sortedMonthlyData.length > 0
      ? [-0.5, sortedMonthlyData.length - 0.5]
      : [0, 1];

  const CustomTooltip = ({
    active,
    payload,
  }: TooltipProps<ValueType, NameType>) => {
    const linePayload = payload?.find((entry) => entry.dataKey === 'amount');

    if (active && linePayload) {
      const month = dayjs(linePayload.payload.date).format('MMM YYYY');

      if (month !== trackTooltipMonth) {
        trackEvent({ eventName: TrackEventName.Tooltip, month });

        setTrackTooltipMonth(month);
      }

      return (
        <ReturnsChartTooltip
          active={active}
          monthlyReturnData={monthlyReturnData}
          payload={[linePayload]}
        />
      );
    }

    return null;
  };

  const barSize = 100;
  const composedChartMargin = {
    top: 15,
    right: 20,
    left: 20,
    bottom: 15,
  };

  const yAxis1 = (
    <YAxis
      domain={[getReturnsAxisMin, getReturnsAxisMax]}
      stroke="none"
      tick={Styled.TickStyle}
      tickFormatter={(value) =>
        moneyFormatter(value, CurrencyVariant.Truncated)
      }
      tickLine={false}
      yAxisId="left"
    />
  );

  const yAxis2 = (
    <YAxis
      axisLine={false}
      domain={['dataMin', 'dataMax']}
      orientation="right"
      stroke="none"
      tick={hiddenTickStyle}
      tickFormatter={(value) =>
        moneyFormatter(value, CurrencyVariant.Truncated)
      }
      tickLine={false}
      yAxisId="right"
    />
  );

  const overlayYAxis1 = (
    <YAxis
      axisLine={false}
      domain={[getReturnsAxisMin, getReturnsAxisMax]}
      stroke="none"
      tick={hiddenTickStyle}
      tickFormatter={(value) =>
        moneyFormatter(value, CurrencyVariant.Truncated)
      }
      tickLine={false}
      yAxisId="left"
    />
  );

  const overlayYAxis2 = (
    <YAxis
      domain={['dataMin', 'dataMax']}
      orientation="right"
      stroke="none"
      tick={Styled.TickStyle}
      tickFormatter={(value) =>
        moneyFormatter(value, CurrencyVariant.Truncated)
      }
      tickLine={false}
      yAxisId="right"
    />
  );

  return (
    <Styled.RechartsContainer
      position="relative"
      py={3}
      height={300}
      width="100%"
    >
      <ResponsiveContainer>
        <ComposedChart data={sortedMonthlyData} margin={composedChartMargin}>
          <CartesianGrid
            stroke={Styled.CartesianGridStroke}
            strokeDasharray="6"
            vertical={false}
          />
          <XAxis
            dataKey="start_date"
            stroke={Styled.XAxisStroke}
            tick={Styled.TickStyle}
            tickFormatter={(value) =>
              dayjs(value).format(getXAxisTickFormat(timeFilter))
            }
            tickLine={false}
            type="category"
            xAxisId="month"
          />
          {yAxis1}
          {yAxis2}
          <Bar
            barSize={barSize}
            dataKey="returns_in_dollars"
            fill={Styled.BarFillManagedInvestments}
            isAnimationActive={false}
            minPointSize={2}
            xAxisId="month"
            yAxisId="left"
          />
        </ComposedChart>
      </ResponsiveContainer>

      <ResponsiveContainer>
        <ComposedChart data={sortedDailyData} margin={composedChartMargin}>
          <XAxis
            axisLine={false}
            dataKey="xIndex"
            domain={xDomain}
            height={30}
            stroke="none"
            tick={false}
            tickLine={false}
            type="number"
            xAxisId="line"
          />
          {overlayYAxis1}
          {overlayYAxis2}
          <Line
            activeDot={false}
            dataKey="amount"
            dot={false}
            isAnimationActive={false}
            stroke={Styled.AUMLineStroke}
            strokeWidth={2}
            type="linear"
            xAxisId="line"
            yAxisId="right"
          />
          <Tooltip content={<CustomTooltip />} shared={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </Styled.RechartsContainer>
  );
};
